import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat, formatDate, PF } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { useEffect, useState } from 'react';
import { CustomerProducts, getProductsByCustomer } from 'api/orders/_requests';
import toast from 'react-hot-toast';
import { addToCart } from 'api/cart/_requests';
import { addProductsToVendor as addToCartAction } from 'redux-state/user/cartSlice';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';

const EComCustomerProductsTable = () => {
  const dispatch = useDispatch();
  const [productsData, setProductsData] = useState<CustomerProducts[]>([]);

  const fetchProductsData = async () => {
    const response = await getProductsByCustomer();

    const temp = response.map((item: CustomerProducts) => ({
      ...item,
      thumbnail: PF + (item.thumbnail !== undefined ? item.thumbnail : 'goods')
    }));

    setProductsData(temp);
  };

  const handleAddToCart = async (product: CustomerProducts) => {
    const data = {
      vendorId: product.vendorId,
      product: {
        productId: product.productId,
        variantId: '',
        price: product.price,
        quantity: 1
      }
    };
    await addToCart(data)
      .then(res => {
        dispatch(
          addToCartAction({
            vendorId: product.vendorId,
            products: {
              productId: product.productId,
              variantId: '',
              price: product.price,
              quantity: 1,
              name: product.name,
              thumbnail: product.thumbnail,
              vendorId: product.vendorId,
              discountedPrice: product.price,
              color: '',
              size: '',
              weight: ''
            },
            totalAmount: product.price,
            totalAmountAfterDiscount: 0
          })
        );
        toast.success('Added to cart');
      })
      .catch(err => {
        toast.error('Failed to add to cart');
      });
  };

  const columns: ColumnDef<CustomerProducts>[] = [
    {
      id: 'productImage',
      accessorKey: '',
      cell: ({ row: { original } }) => {
        const { thumbnail } = original;
        return (
          <div className="rounded-2 border border-translucent d-inline-block">
            <img
              src={thumbnail}
              alt="product"
              width={53}
              className="image-square"
            />
          </div>
        );
      },
      meta: {
        cellProps: { className: 'py-0' },
        headerProps: { style: { width: '7%' } }
      }
    },
    {
      accessorKey: 'product',
      header: 'Products',
      cell: ({ row: { original } }) => {
        const { name } = original;
        return (
          <Link
            to={`/product-details/${original.productId}`}
            className="fw-semibold line-clamp-1"
          >
            {name}
          </Link>
        );
      },
      meta: {
        headerProps: { style: { minWidth: 250, width: '30%' } },
        cellProps: { className: 'pe-11' }
      }
    },
    {
      accessorKey: 'price',
      header: () => 'Price',
      cell: ({ row: { original } }) =>
        currencyFormat(original.price, {
          currency: 'SAR',
          style: 'currency'
        }),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-end' },
        cellProps: { className: 'text-end fw-semibold' }
      }
    },
    {
      accessorKey: 'orderId',
      header: 'Order ID',
      cell: ({ row: { original } }) => {
        const { orderId } = original;
        return (
          <Link to="#!" className="fw-semibold line-clamp-1">
            #{orderId}
          </Link>
        );
      },
      meta: {
        headerProps: { style: { width: '10%' } },
        cellProps: { className: 'pe-11' }
      }
    },
    {
      accessorKey: 'orderDate',
      header: 'Order Date',
      cell: ({ row: { original } }) => {
        const { orderDate } = original;
        return <span className="fw-semibold">{formatDate(orderDate)}</span>;
      },
      meta: {
        headerProps: { style: { width: '20%' } },
        cellProps: { className: 'pe-11' }
      }
    },
    {
      id: 'action',
      cell: ({ row: { original } }) => {
        return (
          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              className="fs-10 text-nowrap"
              startIcon={<FontAwesomeIcon icon={faShoppingCart} />}
              onClick={() => handleAddToCart(original)}
            >
              Add to cart
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '35%' } }
      }
    }
  ];

  useEffect(() => {
    fetchProductsData();
  }, []);

  const table = useAdvanceTable({
    data: productsData,
    columns,
    pageSize: 5,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="border-y border-translucent">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>
    </div>
  );
};

export default EComCustomerProductsTable;
